import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useDispatch, useSelector } from 'react-redux'
import { createAppointment } from './postsActions'
import { swipePost } from '../Swipe/swipeActions' // import swipePost thunk

// Props: open, onClose, swipeId, onSuccess (optional)
export default function AppointmentDialog({ open, onClose, swipeId, onSuccess }) {
  const dispatch = useDispatch()
  // Reset appointmentSuccess in Redux when dialog opens
  useEffect(() => {
    if (open) {
      dispatch({ type: 'RESET_APPOINTMENT_SUCCESS' })
    }
  }, [open, dispatch])
  const { appointmentLoading, appointmentError, appointmentSuccess } = useSelector(state => state.posts)
  const [scheduledTime, setScheduledTime] = useState(new Date())
  const [notes, setNotes] = useState('')
  const [localError, setLocalError] = useState('')

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setScheduledTime(new Date())
      setNotes('')
      setLocalError('')
    }
  }, [open])

  // Close dialog on success
  useEffect(() => {
    if (appointmentSuccess && open) {
      onClose && onClose()
    }
  }, [appointmentSuccess, open, onClose])

  const handleSubmit = async () => {
    setLocalError('')
    if (!scheduledTime || !swipeId) {
      setLocalError('Please provide all required fields.')
      return
    }
    try {
      // 1. Perform swipe right
      const swipeResult = await dispatch(swipePost(swipeId, 'right'))
      // If swipePost returns an error, it will throw
      // 2. Use the returned PK (swipeResult.id) or fallback to swipeId
      const appointmentPk = swipeResult?.id || swipeId
      await dispatch(createAppointment(appointmentPk, scheduledTime.toISOString(), notes))
      // Notify parent to advance card
      if (onSuccess) onSuccess()
    } catch (err) {
      setLocalError(err.message || 'Failed to swipe and create appointment')
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Set Appointment</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Select a date and time for your appointment:</Typography>
          <DateTimePicker
            label="Scheduled Time"
            value={scheduledTime}
            onChange={setScheduledTime}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
            disablePast
          />
          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          {(localError || appointmentError) && <Alert severity="error" sx={{ mt: 2 }}>{localError || appointmentError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={appointmentLoading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={appointmentLoading} color="primary">
            {appointmentLoading ? 'Saving...' : 'Set Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}
